
import { isSSR } from '../../lib/isSSR.js';

// Image cache settings
const DEFAULT_IMAGE = '/placeholder.svg';
const WORDPRESS_IMAGE_DOMAINS = ['blog.gildy.it', 'www.gildy.it'];

// Conditionally import fetchAndCacheImage only in SSR environment
let fetchAndCacheImage = null;

// Initialize image processor in a non-top-level await context
if (isSSR()) {
  // Import using traditional Promise pattern to avoid top-level await
  import('../../lib/imageProcessor/fetchImage.js')
    .then(module => {
      fetchAndCacheImage = module.fetchAndCacheImage;
    })
    .catch(error => {
      console.error('Error importing fetchImage module:', error);
    });
} else {
  // Client-side stub for fetchAndCacheImage to avoid build errors
  fetchAndCacheImage = async () => null;
}

/**
 * Determines if a URL is a WordPress image based on known domains
 */
export const isWordPressImage = (url) => {
  if (!url) return false;
  try {
    // Make sure we're dealing with an absolute URL
    if (!url.startsWith('http')) {
      console.warn('[ImageCache] Not an absolute URL:', url);
      return false;
    }
    
    const urlObj = new URL(url);
    return WORDPRESS_IMAGE_DOMAINS.some(domain => urlObj.hostname.includes(domain));
  } catch (e) {
    console.error('[ImageCache] Invalid URL format:', url, e);
    return false;
  }
};

/**
 * Returns the cached image path for a WordPress image
 */
export const getCachedImagePath = (originalUrl) => {
  if (!originalUrl) {
    console.warn('[ImageCache] Empty URL passed to getCachedImagePath');
    return DEFAULT_IMAGE;
  }
  
  // Only process absolute URLs that are WordPress images
  if (!originalUrl.startsWith('http') || !isWordPressImage(originalUrl)) {
    console.log('[ImageCache] Not a WordPress image, skipping cache:', originalUrl);
    return originalUrl;
  }

  try {
    // Extract filename from URL
    const urlObj = new URL(originalUrl);
    const pathSegments = urlObj.pathname.split('/').filter(Boolean);
    const filename = pathSegments[pathSegments.length - 1];
    
    // Handle empty filename edge case
    if (!filename) {
      console.warn('[ImageCache] Could not extract filename from URL:', originalUrl);
      return originalUrl;
    }
    
    // Return path to image cache API without any parameters
    // We'll add them separately in the component
    const cachedPath = `/api/image-cache/${encodeURIComponent(filename)}?source=${encodeURIComponent(originalUrl)}`;
    
    console.log(`[ImageCache] Original URL: ${originalUrl}`);
    console.log(`[ImageCache] Cache path: ${cachedPath}`);
    
    return cachedPath;
  } catch (e) {
    console.error('[ImageCache] Error creating cached image path:', e);
    return originalUrl;
  }
};

/**
 * Utility function to fetch and process an image URL with optimization parameters
 */
export const getOptimizedImageUrl = (
  imageUrl, 
  width,
  quality,
  format
) => {
  if (!imageUrl) return DEFAULT_IMAGE;
  
  try {
    // Check if the URL is already a local path
    if (imageUrl.startsWith('/api/image-cache/')) {
      return imageUrl;
    }
    
    // Only process absolute URLs
    if (!imageUrl.startsWith('http')) {
      console.warn('[ImageCache] Not an absolute URL for optimization:', imageUrl);
      return imageUrl;
    }
    
    const basePath = getCachedImagePath(imageUrl);
    
    // If not a cached path or in SSR mode, return original
    if (basePath === imageUrl || isSSR()) {
      return imageUrl;
    }
    
    // Create URL object to properly handle parameters
    // Use base window.location for relative URLs
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const url = new URL(basePath, origin);
    
    // Add optimization parameters if provided
    if (width) url.searchParams.set('w', width.toString());
    if (quality) url.searchParams.set('q', quality.toString());
    if (format) url.searchParams.set('f', format);
    
    console.log(`[ImageCache] Optimized URL: ${url.toString()}`);
    return url.toString();
  } catch (e) {
    console.error('[ImageCache] Error generating optimized URL:', e);
    return imageUrl;
  }
};

// Export fetchAndCacheImage function
export { fetchAndCacheImage };
